import { useState } from "react";
import { ClipboardDocumentListIcon, EyeIcon } from "@heroicons/react/24/outline";
import { Button, InfoBox, Loading, Modal, Property, SectionTitle, Tabs, Tag } from "../ui";
import { TYPE_NAMES, formatSize } from "../scholarships/review/model";

const VIEWS = [
  { value: "setup", label: "Full set-up", icon: <ClipboardDocumentListIcon className="h-4 w-4" /> },
  { value: "student", label: "Student page", icon: <EyeIcon className="h-4 w-4" /> },
];

const typesOf = (allowed = []) => allowed.map((t) => TYPE_NAMES[t] ?? t).join(", ") || "—";

function Documents({ requirements }) {
  if (!requirements.length) return <p className="text-sm text-amber-700">No documents yet.</p>;
  return (
    <ul className="flex flex-col divide-y divide-slate-100 rounded-lg border border-slate-100">
      {requirements.map((r) => (
        <li key={r.id ?? r.code} className="flex flex-col gap-1 px-3 py-2 text-sm">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-slate-800">{r.label}</span>
            <Tag color={r.mandatory ? "blue" : "gray"}>{r.mandatory ? "Required" : "Optional"}</Tag>
            <span className="text-slate-500">
              {typesOf(r.allowedTypes)} · up to {formatSize(r.maxBytes)}
            </span>
          </span>
          {r.description && <span className="text-slate-600">{r.description}</span>}
        </li>
      ))}
    </ul>
  );
}

function Criteria({ criteria }) {
  if (!criteria.length) return <p className="text-sm text-amber-700">No marking criteria yet.</p>;
  const total = criteria.reduce((sum, c) => sum + (Number(c.weight) || 0), 0);
  return (
    <div className="flex flex-col gap-2">
      <ol className="flex flex-col divide-y divide-slate-100 rounded-lg border border-slate-100">
        {criteria.map((c, i) => (
          <li key={c.id ?? i} className="flex flex-col gap-1 px-3 py-2 text-sm">
            <span className="flex items-center justify-between gap-3">
              <span className="font-medium text-slate-800">{c.name}</span>
              <span className="font-semibold text-slate-700">{c.weight}%</span>
            </span>
            {c.guideline && <span className="text-slate-600">{c.guideline}</span>}
          </li>
        ))}
      </ol>
      <span className={`self-end text-sm font-semibold ${total === 100 ? "text-green-700" : "text-amber-700"}`}>Weights: {total} / 100</span>
    </div>
  );
}

function Programme({ programme }) {
  return (
    <section className="flex flex-col gap-4 rounded-lg border border-slate-200 p-4">
      <div>
        <h4 className="font-semibold text-slate-900">{programme.name}</h4>
        {programme.meta && <p className="text-sm text-slate-500">{programme.meta}</p>}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-sm font-semibold text-slate-700">Who may apply</span>
        <p className="text-sm text-slate-700">{programme.rules.length ? programme.rules.join("; ") : "Any student"}</p>
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-sm font-semibold text-slate-700">Required documents ({programme.requirements.length})</span>
        <Documents requirements={programme.requirements} />
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-sm font-semibold text-slate-700">Marking criteria</span>
        <Criteria criteria={programme.criteria} />
      </div>
    </section>
  );
}

// A titled list of names, e.g. the committee; `pending` while it loads
function People({ title, people, pending, empty }) {
  return (
    <section className="flex flex-col gap-3">
      <SectionTitle title={`${title}${pending ? "" : ` (${people.length})`}`} />
      {pending ? (
        <Loading label={`Loading ${title.toLowerCase()}…`} />
      ) : people.length ? (
        <ul className="flex flex-wrap gap-2">
          {people.map((p) => (
            <li key={p.key} className="rounded-md bg-slate-100 px-3 py-1 text-sm text-slate-700">
              {p.label}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-amber-700">{empty}</p>
      )}
    </section>
  );
}

/**
 * The whole set-up of a call as saved, before it is published: every detail, each programme with
 * who may apply, its documents and its marking criteria, and the people involved; then the page
 * students will see. Shared by scholarships and abroad loan calls.
 *
 * `details`: [label, value] rows (`wide` ones take the full width); `programmes`: { id, name, meta,
 * rules: string[], requirements, criteria }; `people`: { title, people: [{ key, label }], pending,
 * empty } sections; `studentPage`: the student view.
 */
export default function SetupPreview({ title, details, programmes, people = [], studentPage, onClose }) {
  const [view, setView] = useState("setup");
  return (
    <Modal
      open
      onClose={onClose}
      size="full"
      title={`Preview: ${title}`}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close preview
        </Button>
      }
    >
      <div className="flex flex-col gap-6">
        <Tabs tabs={VIEWS} value={view} onChange={setView} className="self-start" />
        {view === "student" ? (
          studentPage
        ) : (
          <>
            <InfoBox>Everything as saved. Unsaved changes in the tabs are not shown; save them first.</InfoBox>
            <section className="flex flex-col gap-3">
              <SectionTitle title="Details" />
              <div className="grid gap-x-8 md:grid-cols-2">
                {details
                  .filter(([, , wide]) => !wide)
                  .map(([label, value]) => (
                    <Property key={label} label={label} value={value || "—"} />
                  ))}
              </div>
              {details
                .filter(([, , wide]) => wide)
                .map(([label, value]) => (
                  <div key={label} className="flex flex-col gap-1">
                    <span className="text-[15px] font-medium text-slate-600">{label}</span>
                    <p className="whitespace-pre-line text-sm text-slate-800">{value || "—"}</p>
                  </div>
                ))}
            </section>
            <section className="flex flex-col gap-3">
              <SectionTitle title={`Programmes (${programmes.length})`} />
              {programmes.length ? programmes.map((p) => <Programme key={p.id} programme={p} />) : <p className="text-sm text-amber-700">No programmes yet.</p>}
            </section>
            {people.map((section) => (
              <People key={section.title} {...section} />
            ))}
          </>
        )}
      </div>
    </Modal>
  );
}
